import { isAuOrNzRRQuestion } from './isAuOrNzRRQuestion';

interface SimplifiedAnswer {
  id: string;
  globalId?: string;
  text: string;
  answerIds: string[];
  order: number;
}

export interface SimplifiedAnswerAnswerIds {
  id: string;
  globalId?: string;
}

/**
 * IMPORTANT: AuRolledUpRTWAnswers and NzRolledUpRTWAnswers are hardcoded answers for jobs with legacy questionnaire
 * https://seekchat.slack.com/archives/C03EFUD9P/p1728964423082509
 */
export const AuRtwQuestionId = '6';
const AuRolledUpRTWAnswers: SimplifiedAnswer[] = [
  {
    id: '29013',
    globalId: 'AUS_Q_6_V_5_A_29013',
    text: 'I have permanent work rights with no restrictions',
    answerIds: ['14970', '14971'],
    order: 1,
  },
  {
    id: '29014',
    globalId: 'AUS_Q_6_V_5_A_29014',
    text: 'I have temporary work rights with no restrictions',
    answerIds: [
      '14972',
      '14973',
      '14974',
      '14976',
      '28629', // NOTE: old questionnaires have '8916' instead of '28629'
    ],
    order: 2,
  },
  {
    id: '29015',
    globalId: 'AUS_Q_6_V_5_A_29015',
    text: 'I have temporary work rights with restrictions',
    answerIds: ['14975', '14977'],
    order: 3,
  },
  {
    id: '29016',
    globalId: 'AUS_Q_6_V_5_A_29016',
    text: 'I require sponsorship to work for a new employer',
    answerIds: ['14978'],
    order: 4,
  },
];

export const NzRtwQuestionId = '358';
const NzRolledUpRTWAnswers: SimplifiedAnswer[] = [
  {
    id: '29013',
    globalId: 'AUS_Q_6_V_5_A_29013',
    text: 'I have permanent work rights with no restrictions',
    answerIds: ['14980', '14981'],
    order: 1,
  },
  {
    id: '29014',
    globalId: 'AUS_Q_6_V_5_A_29014',
    text: 'I have temporary work rights with no restrictions',
    answerIds: ['14982', '14983', '14984', '14985', '8917'],
    order: 2,
  },
  {
    id: '29015',
    globalId: 'AUS_Q_6_V_5_A_29015',
    text: 'I have temporary work rights with restrictions',
    answerIds: ['14986'],
    order: 3,
  },
  {
    id: '29016',
    globalId: 'AUS_Q_6_V_5_A_29016',
    text: 'I require sponsorship to work for a new employer',
    answerIds: ['14987'],
    order: 4,
  },
];

const simplifiedAnswersMapping: Record<string, SimplifiedAnswer[]> = {
  [AuRtwQuestionId]: AuRolledUpRTWAnswers,
  [NzRtwQuestionId]: NzRolledUpRTWAnswers,
};

export const isSimplifiedAnswerPreferred = (
  simplifiedAnswer: SimplifiedAnswer,
  preferredAnswersIds: string[],
) =>
  simplifiedAnswer.answerIds.every((answerId) =>
    preferredAnswersIds.find(
      (preferredAnswerId) => preferredAnswerId === answerId,
    ),
  );

export const buildSimplifiedAnswers = ({
  preferredAnswersIds,
  questionGlobalId,
  questionId,
}: {
  questionId: string;
  questionGlobalId: string | null;
  preferredAnswersIds: string[];
}) => {
  if (!isAuOrNzRRQuestion(questionGlobalId)) {
    return null;
  }
  const simplifiedAnswers = simplifiedAnswersMapping[questionId];

  if (!simplifiedAnswers) {
    return null;
  }

  return simplifiedAnswers.map((simplifiedAnswer) => ({
    ...simplifiedAnswer,
    isPreferred: isSimplifiedAnswerPreferred(
      simplifiedAnswer,
      preferredAnswersIds,
    ),
  }));
};

export const getHardcodedSimplifiedAnswerText = ({
  answerId,
  questionGlobalId,
  questionId,
}: {
  questionId: string;
  questionGlobalId: string | null;
  answerId: string;
}): string | undefined => {
  if (!isAuOrNzRRQuestion(questionGlobalId)) {
    return undefined;
  }

  const simplifiedAnswers = simplifiedAnswersMapping[questionId];

  if (!simplifiedAnswers) {
    return;
  }

  const simplifiedAnswer = simplifiedAnswers.find((answer) => {
    if (answer.globalId === answerId) {
      return true;
    }
    return answer.answerIds.some((harcodedId) => harcodedId === answerId);
  });
  return simplifiedAnswer?.text;
};
